/**
 * Client state + helpers for งาน (work tasks, docs/api/TASKS.md): the board columns and their Thai
 * labels, due-date tone (overdue / due-soon), Thai date formatting (พ.ศ., เดือนไทย), a light natural
 * quick-add parser, and a shared summary cache that feeds both the nav badge and the dashboard so a
 * page and the badge make one request (PERF-03, like app/reviewQueue.ts).
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import type { TaskLinkType, TaskPriority, TaskStatus, TaskSummary } from '../api/types.js';
import { useSession } from './session.js';

export const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: 'รอทำ',
  doing: 'กำลังทำ',
  review: 'รอตรวจ',
  done: 'ส่งแล้ว',
  blocked: 'ติดปัญหา',
};

/** The Kanban columns, in order (blocked is shown only when it has cards). */
export const BOARD_COLUMNS: TaskStatus[] = ['todo', 'doing', 'review', 'done'];

export const PRIORITY_LABEL: Record<TaskPriority, string> = { low: 'ต่ำ', normal: 'ปกติ', high: 'สูง', urgent: 'ด่วน' };
export const PRIORITY_TONE: Record<TaskPriority, 'neutral' | 'info' | 'warn' | 'danger'> = { low: 'neutral', normal: 'info', high: 'warn', urgent: 'danger' };

export const LINK_LABEL: Record<TaskLinkType, string> = { design: 'งานออกแบบ', product: 'สินค้า', tor: 'TOR', file: 'ไฟล์' };

/** Where a linked target opens (matches chat/notify TARGET_URL + App.tsx routes). */
export function linkHref(targetType: TaskLinkType, id: string): string {
  switch (targetType) {
    case 'design': return `/d/${id}`;
    case 'product': return `/catalog/items/${id}`;
    case 'tor': return `/tor/drafts/${id}`;
    case 'file': return `/files?file=${id}`;
  }
}

const thaiDate = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' });
const thaiDateTime = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' });

/** "2 ต.ค. 2569" — Thai months, Buddhist year (พ.ศ.), as th-TH renders it. */
export function formatDue(v: string | null | undefined, withTime = false): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return (withTime ? thaiDateTime : thaiDate).format(d);
}

export type DueTone = 'overdue' | 'soon' | 'later' | 'none';

/** Red when overdue, amber when due within 2 days, otherwise neutral. `done` tasks never alarm. */
export function dueTone(dueAt: string | null | undefined, status: TaskStatus, now = Date.now()): DueTone {
  if (!dueAt || status === 'done') return 'none';
  const t = new Date(dueAt).getTime();
  if (Number.isNaN(t)) return 'none';
  if (t < now) return 'overdue';
  if (t <= now + 2 * 86400_000) return 'soon';
  return 'later';
}

export const DUE_TONE_CHIP: Record<Exclude<DueTone, 'none'>, 'danger' | 'warn' | 'neutral'> = { overdue: 'danger', soon: 'warn', later: 'neutral' };

/**
 * A tiny natural quick-add: pull a trailing "วันนี้ / พรุ่งนี้ / มะรืน" or "dd/mm" out of the typed
 * title and return the remaining title plus an ISO due date at 17:00 Bangkok (end of a work day).
 * Everything else (assignee, customer) is picked from the quick-add row's own controls.
 */
export function parseQuickAdd(input: string, now = new Date()): { title: string; dueAt: string | null } {
  let title = input.trim();
  let dueAt: string | null = null;
  const at17 = (d: Date) => {
    const bkk = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    return new Date(`${bkk}T17:00:00+07:00`).toISOString();
  };
  const addDays = (n: number) => { const d = new Date(now); d.setDate(d.getDate() + n); return d; };
  const words: { re: RegExp; days: number }[] = [
    { re: /\s*(วันนี้)$/, days: 0 },
    { re: /\s*(พรุ่งนี้|พรุงนี้)$/, days: 1 },
    { re: /\s*(มะรืน|มะรืนนี้)$/, days: 2 },
  ];
  for (const w of words) {
    if (w.re.test(title)) { dueAt = at17(addDays(w.days)); title = title.replace(w.re, '').trim(); return { title, dueAt }; }
  }
  const dm = /\s*(\d{1,2})\/(\d{1,2})$/.exec(title);
  if (dm) {
    const day = Number(dm[1]);
    const month = Number(dm[2]);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      const year = now.getFullYear();
      const d = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T10:00:00+07:00`);
      // A date already past this year means next year.
      if (d.getTime() < now.getTime() - 86400_000) d.setFullYear(year + 1);
      dueAt = at17(d);
      title = title.replace(/\s*(\d{1,2})\/(\d{1,2})$/, '').trim();
    }
  }
  return { title, dueAt };
}

// ---------- shared summary cache (nav badge + dashboard) ----------

const cache = new Map<string, { summary: TaskSummary; at: number }>();
const inflight = new Map<string, Promise<TaskSummary>>();
const listeners = new Set<() => void>();
const TTL_MS = 60_000;
const emit = () => listeners.forEach((fn) => fn());

export function invalidateTaskSummary(w?: string) {
  if (w) cache.delete(w);
  else cache.clear();
  emit();
}

function loadSummary(w: string): Promise<TaskSummary> {
  const running = inflight.get(w);
  if (running) return running;
  const p = api.taskSummary(w)
    .then((s) => { cache.set(w, { summary: s, at: Date.now() }); return s; })
    .finally(() => { inflight.delete(w); emit(); });
  inflight.set(w, p);
  return p;
}

/** The workspace task summary, cached for a minute and shared with the nav badge. */
export function useTaskSummary(opts: { force?: number } = {}): { summary: TaskSummary | null; loading: boolean } {
  const ws = useSession().workspace;
  const w = ws?.id ?? null;
  const [, setTick] = useState(0);
  const [summary, setSummary] = useState<TaskSummary | null>(() => (w ? cache.get(w)?.summary ?? null : null));
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  useEffect(() => {
    if (!w) return;
    const hit = cache.get(w);
    if (hit && Date.now() - hit.at < TTL_MS && !opts.force) { setSummary(hit.summary); return; }
    let alive = true;
    setLoading(true);
    void loadSummary(w).then((s) => { if (alive) { setSummary(s); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [w, opts.force]);
  return { summary, loading };
}

/** Nav / bottom-tab badge: the caller's overdue task count. */
export function useTaskBadge(): number {
  const { summary } = useTaskSummary();
  return summary?.mine.overdue ?? 0;
}
